import _ from 'lodash';
import { ChevronLeftIcon, MenuIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { BLOG_LANGS } from '../../types/blogTypes';
import blogHistoryStore from '../blogStores/blogHistoryStore';
import frameStore from '../stores/frameStore';
import { useBlogContext } from './BlogProvider';
import { BlogSidebarButton } from './BlogSidebar';

const BlogNavbar = observer(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const { lang = '' } = useBlogContext();

    const isRootPath = useMemo(() => {
        return (
            location.pathname === '/' ||
            _.some(BLOG_LANGS, (lang) => location.pathname === `/${lang}`)
        );
    }, [location.pathname]);

    // 일정 수준 이상 스크롤 되었을 때 타이틀을 보여줌
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = _.debounce(() => {
            setIsScrolled(window.scrollY > 56);
        }, 100);

        window.addEventListener('scroll', handleScroll, {
            passive: true,
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className="bg-base-300 fixed top-0 right-0 left-0 z-(--z-index-navbar) flex h-14 flex-row items-center justify-center">
            <div className="relative flex h-full max-w-(--main-max-width) grow items-center justify-between gap-2 px-2">
                <div className="flex-none">
                    {/* 화면 작을 때 보이는 항목들 */}
                    {isRootPath && (
                        <Link
                            to={`/${lang}`}
                            className="hidden-when-desktop ml-2 block"
                            preventScrollReset={false}
                        >
                            <img
                                src="https://static.retirein40s.com/images/blog_logo/r40_logo.svg"
                                alt="logo"
                                className="size-8"
                            />
                        </Link>
                    )}
                    {!isRootPath && (
                        <button
                            className={twMerge('hidden-when-desktop', 'btn btn-square btn-ghost')}
                            onClick={(e) => {
                                e.preventDefault();

                                // go back or home
                                if (blogHistoryStore.hasHistory()) {
                                    blogHistoryStore.pop();
                                    navigate(-1);
                                } else {
                                    navigate(`/${lang}`);
                                }
                            }}
                        >
                            <ChevronLeftIcon className="size-6" />
                        </button>
                    )}
                </div>
                <div className="flex-auto overflow-hidden">
                    <div
                        className={twMerge(
                            'truncate text-lg font-light transition-opacity',
                            isScrolled ? 'opacity-100' : 'opacity-0',
                        )}
                    >
                        {frameStore.currentTitle}
                    </div>
                </div>
                <div className="flex flex-none flex-row items-center">
                    <BlogSidebarButton
                        className={twMerge('hidden-when-desktop', 'btn btn-ghost btn-square')}
                    >
                        <MenuIcon className="size-6" />
                    </BlogSidebarButton>
                </div>
                {/* 화면 클 때 보이는 로고 */}
                <div
                    className={twMerge(
                        'shown-when-desktop',
                        'absolute top-0 -left-(--floating-sidebar-width) hidden h-full w-(--floating-sidebar-width) px-6',
                    )}
                >
                    <Link
                        to={`/${lang}`}
                        className="flex h-full w-40 flex-row items-center gap-2 font-semibold"
                        preventScrollReset={false}
                    >
                        <img
                            src="https://static.retirein40s.com/images/blog_logo/r40_logo.svg"
                            alt="logo"
                            className="size-8"
                        />
                        Retire in 40s
                    </Link>
                </div>
            </div>
        </div>
    );
});

export default BlogNavbar;
